/* eslint-disable react-hooks/exhaustive-deps */
import _ from 'lodash'
import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useImperativeHandle,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSystemSettings, useSmsToken, useEmailToken } from '../../vendor/react-omnitech-api'
import getWhatsappTokenRedirectUrl from '../../helpers/get-whatsapp-token-redirect-url'
import useI18n from '../../hook/use-i18n'
import TokenButtonView from './token-button-view'

function TokenButtonController(props, ref) {
  const { currentLocale } = useI18n()
  const { getSystemSetting } = useSystemSettings()
  const { t } = useTranslation()
  const smsResendTokenInterval = getSystemSetting('account.sms_token.resend_interval')
  const emailResendTokenInterval = getSystemSetting('account.email_token.resend_interval')
  const siteUrl = getSystemSetting('frontend.site_url')
  const {
    getWhatsAppToken,
    sendSmsToken,
    smsTokenCountDown,
    smsTokenDisable,
    resetCounterSmsToken,
  } = useSmsToken(smsResendTokenInterval)
  const {
    sendEmailToken,
    emailTokenCountDown,
    emailTokenDisable,
    resetCounterEmailToken,
  } = useEmailToken(emailResendTokenInterval)

  const {
    phone,
    useWhatsApp = false,
    email,
    onStartRequestToken,
    onSuccess,
    onError,
    ...rest
  } = props

  const [loading, setLoading] = useState(false)
  const [showRedirectModal, setShowRedirectModal] = useState(false)
  const [whatsappTokenData, setWhatsappTokenData] = useState()
  const [qrCodeValue, setQrCodeValue] = useState('')

  const type = useMemo(() => (
    !_.isEmpty(phone) ? 'phone' : 'email'
  ), [phone])

  const requestToken = async ({ useQrCode } = {}) => {
    let result
    try {
      setLoading(true)
      if (!_.isEmpty(phone)) {
        if (useWhatsApp) {
          const { whatsappToken } = await getWhatsAppToken(phone)
          const redirectUrl = getWhatsappTokenRedirectUrl({
            ...whatsappToken,
            getMessage: ({ callbackUrl }) => t('ui.whatsappTokenButton.whatsappMessage', { callbackUrl }),
            origin: siteUrl,
            locale: _.get(currentLocale, 'locale'),
          })
          result = {
            ...whatsappToken,
            redirectUrl,
          }
        } else {
          result = await sendSmsToken(phone)
        }
      } else if (!_.isEmpty(email)) {
        await sendEmailToken(email)
      }
      setShowRedirectModal(false)
      if (useQrCode) {
        setWhatsappTokenData(result)
        _.delay(() => {
          setQrCodeValue(result.redirectUrl)
        }, 200)
      } else if (_.isFunction(onSuccess)) {
        if (
          _.has(result, 'redirectUrl')
          && !_.isEmpty(result.redirectUrl)
        ) {
          _.delay(() => {
            window.open(result.redirectUrl)
          }, 200)
        }
        onSuccess(result)
      }
      return result
    } catch (error) {
      if (_.isFunction(onError)) onError(error)
    } finally {
      setLoading(false)
    }
  }

  useImperativeHandle(ref, () => ({
    resetCounter: !_.isEmpty(email) ? resetCounterEmailToken : resetCounterSmsToken,
  }))

  useEffect(() => (() => {
    resetCounterSmsToken()
    resetCounterEmailToken()
  }), [])

  const onClick = async () => {
    if (
      _.isEmpty(phone)
      && _.isEmpty(email)
    ) return
    if (_.isFunction(onStartRequestToken)) {
      onStartRequestToken()
    }
    if (!_.isEmpty(phone) && useWhatsApp) {
      setShowRedirectModal(true)
    } else {
      requestToken()
    }
  }

  const onContinue = () => {
    requestToken()
  }
  const onContinueOnMobile = () => {
    requestToken({
      useQrCode: true,
    })
  }
  const onContinueOnMobileSuccess = () => {
    setQrCodeValue('')
    if (_.isFunction(onSuccess)) {
      onSuccess(whatsappTokenData)
    }
  }
  const onDismiss = () => {
    setShowRedirectModal(false)
  }

  const viewProps = {
    countDown: type === 'phone' ? smsTokenCountDown : emailTokenCountDown,
    countDownInProgress: type === 'phone' ? smsTokenDisable : emailTokenDisable,
    loading,
    qrCodeValue,
    showRedirectModal,
    type: type === 'phone' && useWhatsApp ? 'whatsapp' : type,
    onClick,
    onContinue,
    onContinueOnMobile,
    onContinueOnMobileSuccess,
    onDismiss,
    ...rest,
  }

  return (
    <TokenButtonView {...viewProps} />
  )
}

export default forwardRef(TokenButtonController)
